// @flow
import * as React from 'react';
import { Button, Dialog, DialogContent, DialogTitle, Stack } from '@mui/material';
import { ModalCloseButton } from '@coverright/ui/shared';
import { useContext, useState } from 'react';
import { useSaveManagerNoteMutation } from '../shared';
import { LoadingButton } from '@mui/lab';
import { RichEditorField } from './rich-editor-field';

interface ContextProps {
  open: (clientId: string, note?: string) => void;
}

const ManagerNoteFormContext = React.createContext<ContextProps>({
  open: () => {}
})

export const useManagerNoteForm = () => useContext(ManagerNoteFormContext)

export function withManagerNoteForm<P>(WrappedComponent: React.FC<P>) {
  return (props: P) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [note, setNote] = useState('');
    const [clientId, setClientId] = useState<string>();
    const [save, {loading}] = useSaveManagerNoteMutation()

    const open = (id: string, n = '') => {
      setClientId(id);
      setNote(n);
      setDialogOpen(true)
    }

    const handleClose = () => {
      setDialogOpen(false)
      setTimeout(() => {
        setClientId(undefined);
        setNote('');
      }, 300);
    }

    const handleSubmit = async () => {
      await save({
        variables: {clientId, managerNote: note},
        refetchQueries: ['salesQualifiedFuture', 'salesQualified', 'followUps', 'followUpsFuture', 'contacted']
      })
      handleClose();
    }

    return <ManagerNoteFormContext.Provider value={{open}}>
      <Dialog fullWidth maxWidth={'sm'} open={dialogOpen} onClose={handleClose}>
        <DialogTitle variant={'h3'}>
          {note ? 'Edit' : 'Create'} manager note
          {dialogOpen && <ModalCloseButton onClick={handleClose} sx={{ position: 'absolute', top: 4, right: 8 }} />}
        </DialogTitle>
        <DialogContent>
          <RichEditorField value={note} onChange={setNote} />

          <Stack gap={2} mt={2} direction={'row'} alignItems={'center'} justifyContent={'flex-end'}>
            <Button onClick={handleClose} variant={'outlined'}>
              Cancel
            </Button>
            <LoadingButton
              loading={loading}
              onClick={handleSubmit}
              variant={'contained'}
            >
              Save
            </LoadingButton>
          </Stack>
        </DialogContent>
      </Dialog>
      <WrappedComponent {...props} />
    </ManagerNoteFormContext.Provider>
  }
}
